import {
  IActivityFeedAudience,
  ISpecificUsers,
  PostVisibilityEnum,
  useGetSpecificAudienceQuery,
} from 'redux-store/api/activity-feed';
import { ISourcedProjectAudiences, TConvertedAudience } from '../types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IUseAudience } from './types';
import { convertProjectAudience, transformAudience } from '../utils';
import { IProjectProfiles } from 'redux-store/api/projects';

const buildInitialAudience = (
  visibility: PostVisibilityEnum,
  specificUsers: Omit<ISpecificUsers, 'customUsers'> | undefined,
  savedAudienceRoles?: number[],
  savedAudienceGroups?: number[],
  savedVisibleProfileIds?: number[],
  projectProfiles?: IProjectProfiles[]
): TConvertedAudience => {
  if (visibility === PostVisibilityEnum.JUST_ME) {
    return {
      postVisibility: PostVisibilityEnum.JUST_ME,
      byRole: [],
      customGroups: [],
      customUsers: [],
    };
  }

  if (visibility === PostVisibilityEnum.SPECIFIC_MEMBERS) {
    const byRole = savedAudienceRoles?.length
      ? (specificUsers?.byRole || []).filter((role) => savedAudienceRoles.includes(role.id))
      : [];

    const customGroups = savedAudienceGroups?.length
      ? (specificUsers?.customGroups || []).filter((group) => savedAudienceGroups.includes(group.id))
      : [];

    const customUsers: IActivityFeedAudience[] =
      savedVisibleProfileIds?.length && projectProfiles
        ? projectProfiles
            .filter((profile) => savedVisibleProfileIds.includes(profile.id))
            .map((profile) => ({
              profileId: profile.id,
              firstName: profile.firstName,
              lastName: profile.lastName,
              roleName: profile.role,
              avatar: profile.avatar || '',
            }))
        : [];

    return {
      postVisibility: PostVisibilityEnum.SPECIFIC_MEMBERS,
      byRole,
      customGroups,
      customUsers,
    };
  }

  return {
    postVisibility: PostVisibilityEnum.EVERYONE,
    customUsers: [],
    byRole: specificUsers?.byRole || [],
    customGroups: specificUsers?.customGroups || [],
  };
};

const getPostVisibilityFromArray = (visibility?: number[]): PostVisibilityEnum => {
  if (!visibility || visibility.length === 0) return PostVisibilityEnum.EVERYONE;
  if (visibility[0] === -1) return PostVisibilityEnum.EVERYONE;
  if (visibility[0] === -2) return PostVisibilityEnum.JUST_ME;
  return PostVisibilityEnum.SPECIFIC_MEMBERS;
};

export const useAudience = ({
  projectId,
  postVisibility,
  visibility,
  userPermissions,
  actionType,
  isDraft,
  audienceGroups,
  audienceRoles,
  visibleProfileIds,
  projectProfiles,
}: IUseAudience) => {
  const workOnceRef = useRef(false);
  const [latestPostAudience, setLatestPostAudience] = useState<TConvertedAudience>();

  const derivedPostVisibility = postVisibility || getPostVisibilityFromArray(visibility);

  const showAudience = useMemo(
    () =>
      ['CREATE_POST', 'UPDATE_POST'].includes(actionType) &&
      userPermissions?.AUDIENCES?.canRead &&
      userPermissions?.ENABLE_ACTIVITY_FEED?.canRead,
    [actionType, userPermissions]
  );

  const { data: specificUsers, isError: specificUsersError } = useGetSpecificAudienceQuery(
    { projectId },
    { skip: !projectId || !showAudience }
  );

  useEffect(() => {
    if (isDraft) return;
    const shouldCreatePost = actionType === 'CREATE_POST' && specificUsers && !workOnceRef.current;
    if (shouldCreatePost) {
      setLatestPostAudience(buildInitialAudience(PostVisibilityEnum.EVERYONE, specificUsers));
      workOnceRef.current = true;
    }
  }, [actionType, specificUsers, isDraft]);

  useEffect(() => {
    if (isDraft) return;
    const shouldUpdatePost = actionType === 'UPDATE_POST' && specificUsers && projectProfiles?.length && !workOnceRef.current;
    if (shouldUpdatePost) {
      setLatestPostAudience(
        buildInitialAudience(derivedPostVisibility, specificUsers, audienceRoles, audienceGroups, visibleProfileIds, projectProfiles)
      );
      workOnceRef.current = true;
    }
  }, [actionType, specificUsers, derivedPostVisibility, isDraft, audienceRoles, audienceGroups, visibleProfileIds, projectProfiles]);

  useEffect(() => {
    const shouldUpdateDraft = isDraft && specificUsers && projectProfiles?.length && !workOnceRef.current;
    if (shouldUpdateDraft) {
      setLatestPostAudience(
        buildInitialAudience(derivedPostVisibility, specificUsers, audienceRoles, audienceGroups, visibleProfileIds, projectProfiles)
      );
      workOnceRef.current = true;
    }
  }, [specificUsers, derivedPostVisibility, isDraft, audienceRoles, audienceGroups, visibleProfileIds, projectProfiles]);

  const convertedSpecificUsers: ISourcedProjectAudiences | null = useMemo(
    () => (showAudience ? convertProjectAudience(specificUsers) : null),
    [specificUsers, showAudience]
  );

  const convertedLatestAudience: TConvertedAudience | null = useMemo(
    () => (showAudience && latestPostAudience ? latestPostAudience : null),
    [latestPostAudience, showAudience]
  );

  const transformedAudience = useMemo(() => {
    return transformAudience(convertedLatestAudience, showAudience);
  }, [convertedLatestAudience, showAudience, actionType]);

  return {
    isError: specificUsersError,
    latestPostAudience,
    convertedSpecificUsers,
    convertedLatestAudience,
    showAudience,
    transformedAudience,
    setLatestPostAudience,
  };
};
